import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, X, Crown, Calendar } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import BadgePicker from "../BadgePicker";

const empty = () => ({
  name: "",
  year: "",
  short: "",
  description_html: "",
  image: "",
  news_header: "",
  is_legendary: false,
  order: 0,
  badges: [],
});

const EventsPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setEvents(await a.cmsListEvents()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load();  }, []);

  const onSave = async () => {
    if (!editing.name.trim()) return toast({ title: "Name fehlt", variant: "destructive" });
    setSaving(true);
    try {
      const payload = {
        ...editing,
        order: Number(editing.order) || 0,
      };
      if (editing.id) await a.cmsUpdateEvent(editing.id, payload);
      else await a.cmsCreateEvent(payload);
      toast({ title: "Event gespeichert" });
      setEditing(null);
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Fehler beim Speichern", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const onDelete = async (ev) => {
    if (!window.confirm(`Event "${ev.name}" wirklich loeschen?`)) return;
    try {
      await a.cmsDeleteEvent(ev.id);
      toast({ title: "Geloescht" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const legendary = events.filter((e) => e.is_legendary);
  const general = events.filter((e) => !e.is_legendary);

  return (
    <div className="space-y-6" data-testid="events-panel">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Events verwalten</h2>
          <p className="mt-1 text-sm text-zinc-400">Allgemeine Habbo-Events + legendaere Events. Badges koennen pro Event hinterlegt werden (werden bei "verspielt"-Events angezeigt).</p>
        </div>
        <Button onClick={() => setEditing(empty())} data-testid="events-new-btn" className="btn-primary rounded-lg px-3 py-2 text-sm">
          <Plus className="mr-2 h-4 w-4" /> Neues Event
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {/* Legendaere */}
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#ffb84d]">
              <Crown className="h-4 w-4" /> Exklusive Events ({legendary.length})
            </h3>
            <EventGrid items={legendary} onEdit={setEditing} onDelete={onDelete} />
          </section>
          {/* Allgemein */}
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-300">
              <Calendar className="h-4 w-4" /> Allgemeine Events ({general.length})
            </h3>
            <EventGrid items={general} onEdit={setEditing} onDelete={onDelete} />
          </section>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0f141b] p-6" data-testid="events-edit-modal">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{editing.id ? "Event bearbeiten" : "Neues Event"}</h3>
              <Button onClick={() => setEditing(null)} className="btn-ghost rounded-md p-1.5"><X className="h-4 w-4" /></Button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400">Name *</label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} maxLength={120} data-testid="events-edit-name" className="bg-[#10141b] border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Jahr</label>
                <Input value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} maxLength={10} placeholder="2007" data-testid="events-year" className="bg-[#10141b] border-white/10 text-white" />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs uppercase tracking-wider text-zinc-400">Kurzbeschreibung</label>
                <Textarea value={editing.short} onChange={(e) => setEditing({ ...editing, short: e.target.value })} rows={2} maxLength={400} data-testid="events-short" className="bg-[#10141b] border-white/10 text-zinc-200" />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs uppercase tracking-wider text-zinc-400">Inhalt (HTML erlaubt)</label>
                <Textarea value={editing.description_html} onChange={(e) => setEditing({ ...editing, description_html: e.target.value })} rows={5} placeholder="<p>Ausfuehrliche Beschreibung...</p>" data-testid="events-content" className="bg-[#10141b] border-white/10 text-zinc-200 font-mono text-xs" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400">Hauptbild URL (klein, fuer Karten – optional)</label>
                <Input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="https://images.habbo.com/..." data-testid="events-image" className="bg-[#10141b] border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Reihenfolge</label>
                <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="events-order" className="bg-[#10141b] border-white/10 text-white" />
              </div>

              {/* Habbo News-Header (Banner) */}
              <div className="md:col-span-3 rounded-lg border border-[#7c4dff]/30 bg-[#7c4dff]/5 p-3">
                <label className="text-xs font-bold uppercase tracking-wider text-[#b39bff]">News-Header (Habbo-Stil Banner)</label>
                <div className="mt-1 text-[11px] text-zinc-400">
                  URL zum breiten Banner-Bild – z.B. das offizielle Habbo News-Header das ihr beim Event im Habbo gesehen habt. Wird oben auf der Event-Detail-Seite + als Card-Header angezeigt.
                </div>
                <Input
                  value={editing.news_header || ""}
                  onChange={(e) => setEditing({ ...editing, news_header: e.target.value })}
                  placeholder="https://images.habbo.com/web_images/habbo-web-articles/..."
                  data-testid="events-news-header"
                  className="mt-2 bg-[#10141b] border-white/10 text-white"
                />
                {editing.news_header && (
                  <div className="mt-2 overflow-hidden rounded-md border border-white/10 bg-[#10141b]">
                    <img src={editing.news_header} alt="News-Header Vorschau" className="hb-pixel-img w-full max-h-32 object-cover" />
                  </div>
                )}
              </div>
              <div className="md:col-span-3 flex items-center gap-3 rounded-lg border border-white/10 bg-[#10141b] p-3">
                <Switch checked={!!editing.is_legendary} onCheckedChange={(v) => setEditing({ ...editing, is_legendary: v })} data-testid="events-legendary" />
                <div className="text-sm text-zinc-300">
                  <div className="flex items-center gap-2 font-semibold text-white"><Crown className="h-3.5 w-3.5 text-[#ffb84d]" /> Exklusives Event</div>
                  <div className="text-xs text-zinc-500">Exklusive Events stehen oben auf der Events-Seite mit eigenem Highlight.</div>
                </div>
              </div>

              {/* Badges */}
              <div className="md:col-span-3">
                <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-400">Verspielte Badges</label>
                <BadgePicker
                  value={editing.badges || []}
                  onChange={(b) => setEditing({ ...editing, badges: b })}
                  testIdPrefix="events-badges"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button onClick={() => setEditing(null)} className="btn-ghost rounded-lg px-4 py-2 text-sm">Abbrechen</Button>
              <Button onClick={onSave} disabled={saving} data-testid="events-save" className="btn-primary rounded-lg px-4 py-2 text-sm">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Speichern
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EventGrid = ({ items, onEdit, onDelete }) => {
  if (items.length === 0) return <div className="rounded-lg border border-white/5 bg-[#0f141b] p-6 text-center text-sm text-zinc-500">Keine Events</div>;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {items.map((ev) => (
        <div key={ev.id} data-testid={`event-card-${ev.id}`} className="rounded-xl border border-white/10 bg-[#10141b] p-4">
          <div className="flex items-start gap-3">
            {ev.image && <img src={ev.image} alt="" className="hb-pixel-img h-12 w-12 shrink-0 rounded bg-[#0a0d12] p-1" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {ev.is_legendary && <Crown className="h-3.5 w-3.5 text-[#ffb84d]" />}
                <div className="truncate text-sm font-bold text-white">{ev.name}</div>
              </div>
              <div className="text-xs text-zinc-500">{ev.year || "—"}</div>
              <div className="mt-1 line-clamp-2 text-xs text-zinc-400">{ev.short}</div>
            </div>
          </div>
          {ev.badges && ev.badges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {ev.badges.slice(0, 6).map((b) => (
                <img key={b.code} src={`https://images.habbo.com/c_images/album1584/${b.code}.gif`} alt={b.code} title={b.name} className="hb-pixel-img h-7 w-7" />
              ))}
              {ev.badges.length > 6 && <span className="self-center text-[10px] text-zinc-500">+{ev.badges.length - 6}</span>}
            </div>
          )}
          <div className="mt-3 flex justify-end gap-1">
            <Button onClick={() => onEdit(ev)} className="btn-ghost rounded-md px-2 py-1 text-xs">Bearbeiten</Button>
            <Button onClick={() => onDelete(ev)} className="btn-ghost rounded-md px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventsPanel;

